/**
 * 我的页：用户设置与配置
 * 顶部：账号（未登录 → 邮箱/密码登录；已登录 → 账号信息 + 退出）
 * 其余：学习会员套餐、关于元紫（点击展开版本与主要功能）
 */
import { useState } from 'react';
import { useChartStore } from '../state/store';
import { useMembershipStore } from '../state/membership-store';
import { isNativeApp } from '@shared/api/screenGuard';
import TtsVoiceSetting from '../components/TtsVoiceSetting';

export default function MinePage() {
  const token = useChartStore((s) => s.token);
  const user = useChartStore((s) => s.user);
  const login = useChartStore((s) => s.login);
  const logout = useChartStore((s) => s.logout);
  const memberLevel = useMembershipStore((s) => s.me?.level ?? 'free');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [aboutOpen, setAboutOpen] = useState(false);

  async function onLogin() {
    if (!email.trim() || !password) {
      setMsg('请输入邮箱和密码');
      return;
    }
    setBusy(true);
    setMsg(null);
    const ok = await login(email.trim(), password);
    setBusy(false);
    if (ok) {
      setEmail('');
      setPassword('');
      setMsg('登录成功，命例已与云端同步');
    } else {
      setMsg('登录失败：请检查邮箱和密码，或稍后重试');
    }
  }

  async function onLogout() {
    setBusy(true);
    await logout();
    setBusy(false);
    setMsg('已退出登录，本地命例保留');
  }

  const inputCls = 'w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500';

  return (
    <div className="flex flex-col gap-3">
      {/* 账号与云同步 */}
      <div className="rounded-lg bg-white px-4 py-3 shadow">
        <div className="font-medium text-slate-800">账号</div>
        {token ? (
          <div className="mt-2 flex items-center justify-between">
            <div className="text-sm text-slate-600">
              <div className="font-medium text-slate-700">{user?.nickname || '我的账号'}</div>
              {user?.email && <div className="mt-0.5 text-xs text-slate-500">{user.email}</div>}
              <div className="mt-0.5 text-xs text-slate-400">已开启命例云端同步</div>
            </div>
            <button
              type="button"
              onClick={() => void onLogout()}
              disabled={busy}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40"
            >
              退出登录
            </button>
          </div>
        ) : (
          <div className="mt-2 flex flex-col gap-2">
            <input
              type="email"
              placeholder="邮箱"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
            />
            <input
              type="password"
              placeholder="密码"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
            />
            <button
              type="button"
              onClick={() => void onLogin()}
              disabled={busy}
              className="rounded-md bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-40"
            >
              {busy ? '登录中…' : '登录'}
            </button>
            {/* 注册入口（独立页面，hash 路由）；忘记密码本期不做（需短信/邮件通道），入口暂隐藏 */}
            <div className="flex items-center justify-between text-sm">
              <a href="#/register" className="text-slate-600 hover:text-slate-900">
                注册账号
              </a>
            </div>
            <div className="text-xs text-slate-400">登录后，命例将自动同步到云端，多端可用；未登录时数据仅保存在本地。</div>
          </div>
        )}
        {msg && <div className="mt-2 text-xs text-slate-500">{msg}</div>}
      </div>
      {/* 学习会员订阅入口（会员为 App 独占；web 隐藏，引导去 App） */}
      {isNativeApp() && (
        <div className="rounded-lg bg-white shadow">
          <a
            href="#/membership"
            className="flex items-center justify-between px-4 py-3 text-sm text-slate-700 hover:bg-slate-50"
          >
            <span>
              学习会员套餐
              <span className="ml-2 rounded-full bg-indigo-50 px-2 py-px text-xs text-indigo-600">
                {memberLevel === 'pro' ? '高级会员' : memberLevel === 'basic' ? '基础会员' : '未订阅'}
              </span>
            </span>
            <span className="text-slate-300">›</span>
          </a>
        </div>
      )}
      {/* 听书音色设置（App 独占：依赖系统 TTS 引擎） */}
      {isNativeApp() && <TtsVoiceSetting />}
      {/* 关于元紫（点击展开版本与主要功能） */}
      <div className="rounded-lg bg-white shadow">
        <button
          type="button"
          onClick={() => setAboutOpen(true)}
          className="flex w-full items-center justify-between px-4 py-3 text-sm text-slate-700 hover:bg-slate-50"
        >
          <span>关于元紫</span>
          <span className="text-slate-300">›</span>
        </button>
      </div>

      {aboutOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-6"
          onClick={() => setAboutOpen(false)}
        >
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-base font-semibold text-slate-800">关于元紫</div>
            <div className="mt-3 space-y-1 text-sm text-slate-600">
              <div>版本：1.0.0</div>
              <div className="pt-1 text-xs font-medium text-slate-400">主要功能</div>
              <ul className="list-disc space-y-0.5 pl-5 text-xs text-slate-500">
                <li>紫微斗数排盘（离线 · 本地引擎）</li>
                <li>大限 / 流年 / 流月 / 流日 / 流时多层推运</li>
                <li>命例管理与云端同步</li>
                <li>学习会员课程</li>
                <li>AI 对话</li>
              </ul>
            </div>
            <button
              type="button"
              onClick={() => setAboutOpen(false)}
              className="mt-4 w-full rounded-md bg-slate-800 py-2 text-sm font-medium text-white hover:bg-slate-700"
            >
              关闭
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
